import { CircularProgress, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { computed, makeObservable, observable, reaction, runInAction, toJS } from 'mobx';

/**
 * Enum para tipos de filtros.
 *
 * @readonly
 * @constant
 * @enum {Object}
 */
export const TIPO_FILTRO = {
    SELECAO: 'Registros que possuem o valor selecionado',
    BOOLEANO: 'Registros que possuem ou não a informação'
};

/**
 * Enum para tipos de exibição de filtros (componente de tela).
 *
 * @readonly
 * @constant
 * @enum {Object}
 */
export const TIPO_EXIBICAO_FILTRO = {
    CHECKBOX: 'Caixa de seleção',
    SELECT: 'Caixa de combinação de opções',
    AUTOCOMPLETE: 'Caixa de combinação de opções, com função de autocompletar'
};

/**
 * Enum para tipos de conjuntos de opções dos filtros.
 *
 * @readonly
 * @constant
 * @enum {Object}
 */
export const TIPO_CONJUNTO_OPCOES_FILTRO = {
    SEM_INTERSECAO: 'Sem possibilidade de interseção entre as opções',
    COM_INTERSECAO: 'Possível interseção entre as opções'
};

export class Filtro {
    /**
     * @desc Store utilizada no filtro, inicializada no construtor
     */
    store;

    /**
     * @desc Ordem do filtro na tela.
     * @type {undefined | number}
     */
    ordem = 0;

    /**
     * Identificador do filtro.
     * @type {undefined | string}
     */
    idFiltro;

    /**
     * Rótulo do filtro na tela.
     *
     * @returns {undefined | string}
     */
    get label() {
        return '';
    }

    /**
     * @desc Tipo de filtro - "selecao" (registros que possuem o valor selecionado) ou "booleano" (registros que possuem ou não a informação).
     * @type {undefined | string}
     */
    tipo;

    /**
     * @desc  Para filtros do tipo "selecao". Forma de exibição do filtro - "select", "checkbox" ou "autocomplete".
     * @type {undefined | string}
     */
    exibicao;

    /**
     * @desc  Para filtros do tipo "selecao". Permite filtrar por mais de um valor.
     * @type {boolean}
     */
    selecaoMultipla = false;

    /**
     * @desc Objeto com ícone e valor do filtro rápido. Pode conter um array com valores e ícones a serem utilizados no filtro rápido com mais de uma opção.
     * {
     *     icone: obrigatório - 'Ícone do filtro para exibição no "Filtro Rápido"',
     *     valor: opcional - 'Caso não tenha opções, determina o valor passado para o filtro pelo filtro rápido.',
     *     opcoes: opcional - 'Array com os ícones e valores a serem utilizados caso haja mais de uma opção para o filtro rápido'
     * }
     *
     * @type {undefined | Object}
     */
    filtroRapido;

    /**
     * @desc Ordem de exibição do filtro rápido.
     * @type {undefined | number}
     */
    ordemFiltroRapido;

    /**
     * @desc Determina se o filtro deve calcular os totalizadores.
     * @type {boolean}
     */
    exibeTotalizadores = true;

    /**
     * @desc Valor selecionado pelo usuário e que será utilizado para filtrar os processos caso não seja passado outro valor no método filtrar.
     * @type {any}
     */
    valor;

    /**
     * @desc Indica se o filtro está sendo carregado.
     * @type {boolean}
     */
    carregando = false;

    /**
     * @desc Se for uma lista, para não carregar vários chips, adiciona somente um chip para representar a lista.
     * @type {boolean}
     */
    ocultaValoresChipFiltroAtivo = false;

    /**
     * @desc Oculta o filtro no painel de filtros.
     * @type {boolean}
     */
    ocultaFiltro = false;

    /**
     * @desc Lista de opções disponíveis no filtro
     * @type {array}
     */
    opcoesFiltro = [];

    constructor(parametros) {
        makeObservable(this, {
            idFiltro: observable,
            exibeTotalizadores: observable,
            valor: observable,
            carregando: observable,
            ativo: computed
        });

        runInAction(() => {
            for (let key in parametros) {
                this[key] = parametros[key];
            }
        });

        // Hack para melhorar o lag na renderização das listas de processos
        if (this.store && this.store.processosSessao) {
            this.store.createReaction(
                reaction(
                    () => this.valor,
                    () => {
                        if (this.ativo) {
                            this.store.carregandoProcessos = true;
                            setTimeout(() => runInAction(() => (this.store.carregandoProcessos = false)), 100);
                        }
                    }
                )
            );
        }
    }

    get ativo() {
        return this.valor !== undefined;
    }

    // INÍCIO - Métodos que podem ser sobrescritos nas classes de filtro

    /**
     * Permite desabilitar um filtro em alguma condição específica.
     *
     * @returns {boolean}
     */
    get isHabilitado() {
        return true;
    }

    /**
     * Retorna a descrição correspondente ao valor.
     *
     * Para filtros do tipo "selecao" - Função que recebe o valor e retorna o label correspondente.
     * Para filtros do tipo "booleano" - Função que retorna o respectivo label para os valores "true" e "false".
     * Caso não tenha sido informado, usa como label o próprio valor.
     *
     * @param {*} valor
     * @returns {string}
     */
    getDescricao(valor) {
        return valor;
    }

    /**
     * Retorna o valor da opção do filtro correspondente ao registro no array de processos.
     * Pode retornar um valor, um objeto ou um array de valores ou de objetos. No caso dos filtros booleanos é retornado um valor booleano.
     *
     * @param {*} registro
     * @returns {*}
     */
    getValorOpcao(registro) {
        return registro;
    }

    /**
     * Retorna o tipo do totalizador.
     *
     * @returns {*}
     */
    get tipoConjuntoOpcoes() {
        return TIPO_CONJUNTO_OPCOES_FILTRO.SEM_INTERSECAO;
    }

    /**
     * Retorna o ID (ou um array de IDs) da(s) opção(ões) que representa(m) o totalizador do agregador de opções OU undefined se não houver uma opção que represente a união de todas as opções.
     *
     * @returns {undefined | Object | Object[]}
     */
    get totalizador() {
        return undefined;
    }

    /**
     * Função opcional que retorna o ícone correspondente ao valor passado como parâmetro.
     *
     * @param {undefined | Object} registro
     * @param {undefined | Object | string | number} [valor]
     * @returns {undefined | Object}
     */
    // eslint-disable-next-line no-unused-vars
    getIconeOpcao(registro, valor) {
        return undefined;
    }

    /**
     * Função opcional que retorna a cor da opção correspondente ao registro passado como parâmetro.
     *
     * @param {Object} registro
     * @param {undefined | Object | string | number} valor
     * @returns {undefined | Object}
     */
    // eslint-disable-next-line no-unused-vars
    getCorOpcao(registro, valor) {
        return 'black';
    }

    /**
     * Pode ser implementado na classe de filtro para comparar valores de objetos.
     *
     * @param {*} valor1
     * @param {*} valor2
     * @returns
     */
    equals(valor1, valor2) {
        return valor1 === valor2;
    }

    /**
     * Pode ser implementado na classe de filtro para ordenar valores de objetos.
     *
     * @param {*} opcao1
     * @param {*} opcao2
     * @returns
     */
    sort(opcao1, opcao2) {
        return opcao1.texto.toString().localeCompare(opcao2.texto);
    }

    /**
     * Permite identificar quando o filtro estiver carregando dados.
     *
     * @returns {boolean}
     */
    get carregandoDadosComplementares() {
        return false;
    }

    /**
     * Permite carregar dados complementares aos constantes na listagem de processos.
     */
    async carregarDadosComplementares() {
        return undefined;
    }

    /**
     * Permite apresentar para o filtro informações relacionadas ao pré-carregamento de dados necessários.
     *
     * @returns {undefined | Object}
     */
    renderCarregando() {
        return undefined;
    }

    /**
     * Permite pré-filtrar ou aplicar um filtro adicional nos processos.
     *
     * @param {*} processos
     * @returns
     */
    preFiltrar(processos) {
        return processos;
    }

    // FIM - Métodos que podem ser sobrescritos nas classes de filtro

    /**
     * Filtra os processos passados com base nas configurações do filtro.
     * O valor é opcional e, caso não seja passado, o método irá utilizar o valor atribuído ao filtro.
     *
     * @param {*} processos
     * @param {*} [valor]
     * @returns
     */
    filtrar = (processos, valor) => {
        valor = valor !== undefined ? valor : this.valor;
        if (valor !== undefined) {
            if (this.tipo === 'booleano') {
                return this._filtrarProcessosBooleano(processos, valor);
            }
            return this._filtrarProcessos(processos, valor);
        }
        return processos;
    };

    _filtrarProcessos = (processos, valorParaFiltrar) => {
        let retorno = [];
        if (valorParaFiltrar !== undefined && processos && processos.length > 0) {
            valorParaFiltrar = toJS(valorParaFiltrar);
            let isValorFiltroArray = Array.isArray(valorParaFiltrar);
            let valorOpcao;
            processos = this.preFiltrar(processos);
            for (const registro of processos) {
                valorOpcao = this.getValorOpcao(registro);
                if (valorOpcao !== undefined) {
                    if (Array.isArray(toJS(valorOpcao))) {
                        if (
                            valorOpcao.findIndex((valor) =>
                                this._compararValores(valor, valorParaFiltrar, isValorFiltroArray)
                            ) !== -1
                        ) {
                            retorno.push(registro);
                        }
                    } else {
                        if (this._compararValores(valorOpcao, valorParaFiltrar, isValorFiltroArray)) {
                            retorno.push(registro);
                        }
                    }
                }
            }
        }
        return retorno;
    };

    _compararValores(valorRegistro, valorParaFiltrar, isValorFiltroArray) {
        if (isValorFiltroArray) {
            return valorParaFiltrar.findIndex((valor) => this.equals(valor, valorRegistro)) !== -1;
        }
        return this.equals(valorRegistro, valorParaFiltrar);
    }

    _filtrarProcessosBooleano = (processos, valorParaFiltrar) => {
        if (valorParaFiltrar !== undefined) {
            let valor;
            processos = this.preFiltrar(processos);
            processos = processos.filter((registro) => {
                valor = this.getValorOpcao(registro);
                return (valorParaFiltrar && valor) || (!valorParaFiltrar && !valor);
            });
        }
        return processos;
    };

    labelBooleanoComSem = (booleano) => (booleano ? 'COM' : 'SEM');

    labelBooleanoSimNao = (booleano) => (booleano ? 'SIM' : 'NÃO');

    renderCircularProgress(descricao) {
        return (
            <div style={{ display: 'flex', margin: '8px' }}>
                <CircularProgress size={25} />
                <Typography
                    color={grey[500]}
                    variant="subtitle1"
                    sx={{ ml: 1, fontStyle: 'italic' }}
                >
                    {`Carregando ${descricao}`}
                </Typography>
            </div>
        );
    }

    getOpcoesFiltro = (...atributos) => {
        return this.opcoesFiltro.map((item) => Object.fromEntries(atributos.map((chave) => [chave, item[chave]])));
    };

    getIconeOpcaoFiltroRapido = (valor) => {
        return this.opcoesFiltro.find((opcao) => opcao.valor === valor)?.icone;
    };
}
